import type { Achado } from '@social/shared';

/**
 * Cliente da auditoria pública anônima (`POST /api/auditoria`).
 *
 * Caminho relativo (`/api/auditoria`): em desenvolvimento o Vite proxeia
 * (`vite.config.ts`), em produção o nginx do site proxeia (`nginx.conf`) —
 * o mesmo padrão de `apps/web/src/lib/api.ts`. `buscar` é injetável só para
 * o teste não depender de `fetch` global nem de rede de verdade.
 */
export interface ResumoAchados {
  impeditivos: number;
  exigencias: number;
  avisos: number;
  total: number;
}

export interface RespostaAuditoriaPublica {
  achados: Achado[];
  resumo: ResumoAchados;
}

/**
 * Erro da auditoria pública. `retryAfterSegundos` só vem preenchido nos dois
 * códigos que mandam o cabeçalho `Retry-After` (`limite_ip` 429, `sobrecarga`
 * 503) — o resto fica `undefined` e a tela não mostra contagem nenhuma.
 */
export class ErroAuditoria extends Error {
  constructor(
    readonly codigo: string,
    mensagem: string,
    readonly status: number,
    readonly retryAfterSegundos?: number,
  ) {
    super(mensagem);
    this.name = 'ErroAuditoria';
  }
}

interface EnvelopeErro {
  erro?: { codigo?: string; mensagem?: string };
}

export interface DependenciasAuditoria {
  buscar: typeof fetch;
}

/**
 * Envia o `.docx` para a auditoria pública e devolve o laudo.
 *
 * Sem cabeçalho `content-type` manual: o `FormData` com um `File` já faz o
 * navegador gerar o `multipart/form-data` com o `boundary` certo — declarar
 * na mão quebra o parse do lado do servidor.
 */
export async function enviarAuditoria(
  arquivo: File,
  dep: DependenciasAuditoria = { buscar: fetch },
): Promise<RespostaAuditoriaPublica> {
  const corpo = new FormData();
  corpo.append('arquivo', arquivo);

  const resposta = await dep.buscar('/api/auditoria', { method: 'POST', body: corpo });
  const json = (await resposta.json().catch(() => ({}))) as EnvelopeErro &
    Partial<RespostaAuditoriaPublica>;

  if (!resposta.ok) {
    const retryAfter = resposta.headers.get('retry-after');
    throw new ErroAuditoria(
      json.erro?.codigo ?? 'desconhecido',
      json.erro?.mensagem ?? 'Não foi possível concluir. Tente novamente.',
      resposta.status,
      retryAfter ? Number(retryAfter) : undefined,
    );
  }

  return json as RespostaAuditoriaPublica;
}
